import React from 'react';
import { Typography, Skeleton, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { ReactComponent as FacebookIcon } from 'assets/svg/facebook.svg';
import { ReactComponent as InstagramIcon } from 'assets/svg/instagram.svg';
import { ReactComponent as LinkedinIcon } from 'assets/svg/linkedin.svg';
import { ReactComponent as TelegramIcon } from 'assets/svg/telegram.svg';
import { ReactComponent as TwitterXIcon } from 'assets/svg/twitterx.svg';
import { ReactComponent as LinkIcon } from 'assets/svg/link.svg';
import styles from '../Profile.module.css';

const { Title } = Typography;

interface UserLink {
  id: string;
  url: string;
  user: string;
  link_type: 'IG' | 'TG' | 'FB' | 'IN' | 'XX' | 'OT';
  link_url: string;
}

interface SocialMediaSectionProps {
  userLinks: UserLink[];
  isLoading: boolean;
}

const SocialMediaSection: React.FC<SocialMediaSectionProps> = ({ userLinks, isLoading }) => {
  const { t } = useTranslation();

  const getSocialIcon = (linkType: string) => {
    const normalizedType = linkType?.trim().toUpperCase();

    switch (normalizedType) {
      case 'FB':
        return <FacebookIcon />;
      case 'IG':
        return <InstagramIcon />;
      case 'IN':
        return <LinkedinIcon />;
      case 'XX':
        return <TwitterXIcon />;
      case 'TG':
        return <TelegramIcon />;
      case 'OT':
      default:
        return <LinkIcon />;
    }
  };

  const getSocialColor = (linkType: string) => {
    const normalizedType = linkType?.trim().toUpperCase();

    switch (normalizedType) {
      case 'FB':
        return 'linear-gradient(135deg, #6BB6FF, #4285F4)'; // PANTONE 542U Blue
      case 'IG':
        return 'linear-gradient(135deg, #E94B3C, #D63384)'; // PANTONE 138U Red-Orange
      case 'IN':
        return 'linear-gradient(135deg, #0077b5, #6BB6FF)'; // Professional Blue
      case 'XX':
        return 'linear-gradient(135deg, #1da1f2, #6BB6FF)'; // Twitter Blue
      case 'TG':
        return 'linear-gradient(135deg, #0088cc, #6BB6FF)'; // Telegram Blue
      case 'OT':
      default:
        return 'linear-gradient(135deg, #9BC53D, #7BA428)'; // PANTONE 397U Primary
    }
  };

  const handleSocialClick = (url: string) => {
    window.open(url, '_blank');
  };

  // Don't render if no links available
  if (!isLoading && (!userLinks || userLinks.length === 0)) {
    return null;
  }

  return (
    <Card className={styles.sectionCard} bordered={false}>
      <Title level={4} className={styles.sectionTitle}>
        {t('#Profile.social_media')}
      </Title>

      <Skeleton loading={isLoading} paragraph={{ rows: 1 }} active>
        <div className={styles.socialMediaContainer}>
          {userLinks?.map((link) => {
            // Only render if link_url is not empty
            if (!link.link_url || link.link_url.trim() === '') {
              return null;
            }

            return (
              <div
                key={link.id}
                className={styles.socialIconContainer}
                style={{ background: getSocialColor(link.link_type) }}
                onClick={() => handleSocialClick(link.link_url)}
                title={`Visit ${link.link_type} profile`}
              >
                {getSocialIcon(link.link_type)}
              </div>
            );
          })}
        </div>
      </Skeleton>
    </Card>
  );
};

export default SocialMediaSection;